import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { JobListOrderByEnum, JobRead } from "@nens/rana/api-v1";
import { RanaAlert, RanaHeading, RanaTable } from "@nens/ranalib";
import { useAnimationFrame } from "@nens/react/hooks";
import { capitalizeFirst } from "@nens/shared-utils";

import { useIconSrc } from "@/hooks/use-icon-src";
import { Profile } from "@/lib/profile";
import { nameColumnString } from "@/translations";
import { getPrettyTime, getPrettyTimeFromNow } from "@/utils/get-pretty-time";
import { jobDuration } from "@/utils/job-duration";
import { jobStatusToChakraStatus } from "@/utils/job-status-to-chakra-status";

import { useJobList } from "../hooks/use-job-list";

interface ProcessListProps {
  setJob: (job: JobRead) => void;
  setSourceJob: (job: JobRead) => void;
}

export function JobList({ setJob, setSourceJob }: ProcessListProps) {
  const intl = useIntl();

  const icon = useIconSrc("job");

  const {
    hasJobs,
    processDropdown,
    contributorDropdown,
    page,
    limit,
    setPage,
    orderings,
    setPageSize,
    setOrdering,
    isLoading,
    jobs,
    tableDataIds,
    numPages,
  } = useJobList();

  return (
    <>
      <RanaHeading mt={11} mb={4} type="h4">
        <FormattedMessage id="joblist.title" defaultMessage="Recently ran Analysis tools" />
      </RanaHeading>
      <RanaTable
        ordering={orderings}
        setOrdering={setOrdering}
        columnDefinitions={[
          {
            width: "table_cell_xxxs",
            title: intl.formatMessage({ id: "column.status", defaultMessage: "Status" }),
          },
          {
            width: "table_cell",
            title: nameColumnString(intl),
          },
          {
            width: "table_cell_sm",
            title: intl.formatMessage({
              id: "column.process_name",
              defaultMessage: "Process name",
            }),
          },
          {
            width: "table_cell_xxs",
            title: intl.formatMessage({ id: "column.who", defaultMessage: "Who" }),
          },
          {
            width: "table_cell_xs",
            title: intl.formatMessage({ id: "column.when", defaultMessage: "When" }),
            orderingField: JobListOrderByEnum.StartedAt,
          },
          {
            width: "table_cell_xxs",
            title: intl.formatMessage({ id: "column.took", defaultMessage: "Took" }),
          },
        ]}
        tableDataIds={tableDataIds}
        isSkeleton={isLoading}
        emptyElement={
          <RanaTable.Empty icon={icon}>
            {hasJobs ? (
              <FormattedMessage
                id="emptyElement.jobs_no_search_results"
                defaultMessage="No ran processes found with the current search criteria."
              />
            ) : (
              <FormattedMessage
                id="emptyElement.no_jobs"
                defaultMessage="Ran processes will appear here."
              />
            )}
          </RanaTable.Empty>
        }
        hasRowActions
      >
        <RanaTable.Filters dropdowns={[processDropdown, contributorDropdown]} />

        <RanaTable.Container>
          <RanaTable.Header />
          <RanaTable.Body>
            {jobs.map((job) => {
              const { id, name, process, createdAt, state, creator, startedAt, finishedAt } = job;
              const chakraStatus = jobStatusToChakraStatus(state.type);
              return (
                <RanaTable.Row
                  isDisabled={!process}
                  title={
                    process
                      ? undefined
                      : intl.formatMessage({
                          id: "joblist.no_process_warning",
                          defaultMessage: "Process has been deleted",
                        })
                  }
                  onClick={() => setJob(job)}
                  key={id + ""}
                  rowId={id + ""}
                  rowActions={
                    job.process
                      ? [
                          {
                            label: intl.formatMessage({
                              id: "process.go_to_copy_process_run",
                              defaultMessage: "Go to copy of process run",
                            }),
                            action: () => setSourceJob(job),
                          },
                        ]
                      : undefined
                  }
                >
                  <RanaTable.Cell title={capitalizeFirst(state.type)}>
                    <RanaAlert
                      tooltipLabel={job.state.message ?? undefined}
                      progress={(job?.state.progress ?? 0) * 100}
                      variant="icon-only"
                      iconSize={6}
                      status={chakraStatus}
                    />
                  </RanaTable.Cell>
                  <RanaTable.Cell>{name}</RanaTable.Cell>
                  <RanaTable.Cell>{process?.name}</RanaTable.Cell>
                  <RanaTable.Cell>
                    <Profile inTable user={creator} />
                  </RanaTable.Cell>
                  <RanaTable.Cell title={getPrettyTime(createdAt)}>
                    {getPrettyTimeFromNow(createdAt)}
                  </RanaTable.Cell>
                  <RanaTable.Cell>
                    {startedAt &&
                      (finishedAt ? (
                        jobDuration(startedAt, finishedAt)
                      ) : (
                        <DisplayProgress createdAt={createdAt} startedAt={startedAt} />
                      ))}
                  </RanaTable.Cell>
                </RanaTable.Row>
              );
            })}
          </RanaTable.Body>
        </RanaTable.Container>
        <RanaTable.Pagination
          limit={limit}
          setPageSize={setPageSize}
          page={page}
          numPages={numPages}
          setPage={setPage}
        />
      </RanaTable>
    </>
  );
}

export const DisplayProgress = ({ createdAt, startedAt }: { createdAt: Date; startedAt: Date }) => {
  const [nowDate, setNowDate] = useState(new Date());

  useAnimationFrame(() => {
    if (createdAt) setNowDate(new Date());
  }, 2);

  return jobDuration(startedAt, nowDate);
};
